import React, { useState } from "react";
import { Link } from "react-router-dom";
import DataTable from "react-data-table-component";
import JSZip from "jszip";
import { FiArrowRight, FiTrash2, FiAlertTriangle, FiX, FiDownload } from "react-icons/fi";
import { ImSpinner } from "react-icons/im";
import { displaySchoolTerm } from "../utils/schoolYearUtils";
import { formatCurrency } from "../utils/currencyUtils";
import { exportToCSV } from "../utils/exportCSV";
import { generateInvoicePDF } from "../utils/generateInvoicePDF";
import { userRequest } from "../utils/requestMethods";
import { useStateContext } from "../contexts/ContextProvider";
import CustomPagination from "./CustomPagination/CustomPagination";
import BulkInvoiceDownloadModal from "./BulkInvoiceDownloadModal";

const customStyles = {
  headRow: {
    style: {
      backgroundColor: "rgb(248, 250, 252)",
      fontWeight: "bold",
    },
  },
};

// Standalone confirmation modal
const DeleteConfirmModal = ({ payment, onConfirm, onCancel, deleting }) => {
  const studentName = payment?.child
    ? `${payment.child.firstname} ${payment.child.lastname}`
    : "this student";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm overflow-hidden">
        {/* Red top accent */}
        <div className="h-1.5 w-full bg-red-500" />

        <div className="px-6 py-6">
          {/* Icon */}
          <div className="flex justify-center mb-4">
            <div className="w-14 h-14 rounded-full bg-red-100 flex items-center justify-center">
              <FiAlertTriangle size={26} className="text-red-500" />
            </div>
          </div>

          {/* Text */}
          <h3 className="text-base font-bold text-gray-900 text-center mb-1">Delete Invoice?</h3>
          <p className="text-sm text-gray-500 text-center">
            You're about to permanently delete the invoice for{" "}
            <span className="font-semibold text-gray-800">{studentName}</span>
            {payment?.session && (
              <>
                {" "}
                — {displaySchoolTerm(payment.term)}, {payment.session}
              </>
            )}
            .
          </p>
          <p className="text-xs text-red-500 text-center mt-2 font-medium">
            This action cannot be undone.
          </p>

          {/* Buttons */}
          <div className="flex gap-3 mt-6">
            <button
              type="button"
              onClick={onCancel}
              disabled={deleting}
              className="flex-1 px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={onConfirm}
              disabled={deleting}
              className="flex-1 px-4 py-2.5 text-sm font-semibold text-white bg-red-500 hover:bg-red-600 rounded-xl transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
            >
              {deleting ? (
                <>
                  <ImSpinner className="animate-spin text-sm" />
                  Deleting...
                </>
              ) : (
                <>
                  <FiTrash2 size={14} />
                  Delete Invoice
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const TransactionTable = ({ transactions, loading, nameOfClass, onDeleteInvoice }) => {
  const { school, schoolId } = useStateContext();
  const [filterText, setFilterText] = useState("");
  const [pendingDelete, setPendingDelete] = useState(null); // holds the full payment row
  const [deleting, setDeleting] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);
  const [clearSelectionToggle, setClearSelectionToggle] = useState(false);

  const [bulkDownload, setBulkDownload] = useState(null); // { total, completed, failed, isDone, isDownloading }

  const handleDeleteClick = (row) => setPendingDelete(row);
  const handleCancelDelete = () => setPendingDelete(null);

  const handleConfirmDelete = async () => {
    if (!onDeleteInvoice || !pendingDelete) return;
    setDeleting(true);
    try {
      await onDeleteInvoice(pendingDelete._id);
    } finally {
      setDeleting(false);
      setPendingDelete(null);
    }
  };

  const handleSelectedRowsChange = ({ selectedRows: rows }) => setSelectedRows(rows);

  const CONCURRENCY = 5;
  const BATCH_SIZE = 50;

  const handleBulkDownload = async () => {
    if (selectedRows.length === 0) return;

    setBulkDownload({
      total: selectedRows.length,
      completed: 0,
      failed: [],
      isDone: false,
      isDownloading: false,
    });

    const zip = new JSZip();
    let completed = 0;
    const failed = [];

    const rowById = new Map(selectedRows.map((row) => [row._id, row]));

    // Fetch invoice data (totalPaid, studentAccount) for all selected payments in
    // batches, then render PDFs client-side — same data source, one round-trip per
    // batch instead of one per invoice.
    for (let i = 0; i < selectedRows.length; i += BATCH_SIZE) {
      const batchIds = selectedRows.slice(i, i + BATCH_SIZE).map((row) => row._id);

      let results = [];
      try {
        const { data } = await userRequest.post(
          `/transactions/payment/${schoolId}/bulk-invoice-data`,
          {
            paymentIds: batchIds,
          },
        );
        results = data.results || [];
      } catch (error) {
        results = batchIds.map((paymentId) => ({
          paymentId,
          status: "failed",
          error: error.message,
        }));
      }

      const resultQueue = [...results];
      const renderOne = async (result) => {
        const row = rowById.get(result.paymentId);
        const studentName = row?.child
          ? `${row.child.firstname} ${row.child.lastname}`
          : result.paymentId;
        if (result.status !== "success") {
          failed.push({ id: result.paymentId, label: studentName });
        } else {
          try {
            const { fileName, blob } = await generateInvoicePDF({
              school,
              payment: result.payment,
              studentAccount: result.studentAccount,
              totalPaid: result.totalPaid,
              returnBlob: true,
            });
            zip.file(fileName, blob);
            completed += 1;
          } catch (error) {
            failed.push({ id: result.paymentId, label: studentName });
          }
        }
        const completedSoFar = completed;
        const failedSoFar = [...failed];
        setBulkDownload((prev) =>
          prev ? { ...prev, completed: completedSoFar, failed: failedSoFar } : prev,
        );
      };

      const workers = Array.from(
        { length: Math.min(CONCURRENCY, resultQueue.length) },
        async () => {
          while (resultQueue.length > 0) {
            const result = resultQueue.shift();
            await renderOne(result);
          }
        },
      );
      await Promise.all(workers);
    }

    if (completed > 0) {
      setBulkDownload((prev) => (prev ? { ...prev, isDownloading: true } : prev));
      const zipBlob = await zip.generateAsync({ type: "blob" });
      const url = URL.createObjectURL(zipBlob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${nameOfClass || "Invoices"}.zip`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    }

    setBulkDownload({
      total: selectedRows.length,
      completed,
      failed,
      isDone: true,
      isDownloading: false,
    });
  };

  const handleCloseBulkDownload = () => {
    setBulkDownload(null);
    setSelectedRows([]);
    setClearSelectionToggle((prev) => !prev);
  };

  const columns = [
    {
      name: "Parent Name",
      cell: (row) => (row.user ? `${row?.user?.firstname} ${row?.user?.lastname}` : "N/A"),
    },
    {
      name: "Student Name",
      cell: (row) => (row.child ? `${row.child.firstname} ${row.child.lastname}` : "N/A"),
    },
    {
      name: "Student Class",
      cell: (row) => row?.class?.class_name,
    },
    {
      name: "Total Fees",
      cell: (row) => formatCurrency(row?.total_amount),
    },
    {
      // A granted discount is folded into `amount_paid` on the backend (it
      // counts as settled the moment it's given), so un-bundle it here —
      // otherwise this column repeats the same figure the Discount column
      // already shows.
      name: "Amount Paid",
      cell: (row) =>
        formatCurrency(
          Math.max(0, Number(row?.amount_paid || 0) - Number(row?.discount?.amount || 0)),
        ),
    },
    {
      name: "Discount",
      cell: (row) => formatCurrency(row?.discount?.amount || 0),
    },
    {
      name: "Term",
      cell: (row) => displaySchoolTerm(row?.term) + " - " + row?.session,
    },
    {
      cell: (row) => (
        <div className="flex items-center gap-2">
          {onDeleteInvoice && (
            <button
              onClick={() => handleDeleteClick(row)}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-red-600 bg-red-50 border border-red-200 hover:bg-red-100 rounded-lg transition-colors"
              title="Delete invoice"
            >
              <FiTrash2 size={13} />
              Delete
            </button>
          )}
          <Link to={`/class-transaction/payment/${row?._id}/${row?.child?._id}`}>
            <button
              style={{ color: "#1e4db7", backgroundColor: "#91a6f5" }}
              className="text-md opacity-0.9 rounded-full p-3 hover:drop-shadow-xl"
            >
              <FiArrowRight />
            </button>
          </Link>
        </div>
      ),
      ignoreRowClick: true,
      button: true,
      width: "160px",
    },
  ];

  const filteredData = transactions?.filter(
    (item) =>
      item.child?.firstname?.toLowerCase().includes(filterText.toLowerCase()) ||
      "" ||
      item.child?.lastname?.toLowerCase().includes(filterText.toLowerCase()) ||
      "" ||
      item.user?.firstname?.toLowerCase().includes(filterText.toLowerCase()) ||
      "" ||
      item.user?.lastname?.toLowerCase().includes(filterText.toLowerCase()) ||
      "",
  );

  return (
    <>
      <div className="rounded-2xl">
        <div className="flex justify-between items-center mb-4">
          <input
            type="text"
            className="px-4 py-2 rounded-lg bg-gray-100 border-gray-100 text-gray-900"
            placeholder="Search..."
            value={filterText}
            onChange={(e) => setFilterText(e.target.value)}
          />
          <div className="flex items-center gap-2">
            {selectedRows.length > 0 && (
              <button
                onClick={handleBulkDownload}
                className="inline-flex items-center gap-2 justify-center py-1 px-6 shadow-sm text-sm font-medium rounded-full text-white bg-green-600 hover:bg-green-700"
              >
                <FiDownload size={14} />
                Download Selected ({selectedRows.length})
              </button>
            )}
            <button
              onClick={() => exportToCSV(filteredData, columns, `${nameOfClass}-payments.csv`)}
              className="inline-flex justify-center py-1 px-8 shadow-sm text-sm font-medium rounded-full text-white bg-indigo-600 hover:bg-indigo-700 float-right"
            >
              Export to CSV
            </button>
          </div>
        </div>
        <DataTable
          data={filteredData}
          columns={columns}
          getRowId={(row) => row._id}
          selectableRows
          selectableRowsHighlight
          onSelectedRowsChange={handleSelectedRowsChange}
          clearSelectedRows={clearSelectionToggle}
          pagination
          paginationComponentOptions={{
            rowsPerPageText: "Rows per page:",
            rangeSeparatorText: "of",
            selectAllRowsItem: true,
            selectAllRowsItemText: "All",
          }}
          paginationComponent={CustomPagination}
          paginationRowsPerPageOptions={[10, 20, 30, 50]}
          customStyles={customStyles}
          progressPending={loading}
          progressComponent={<ImSpinner className="animate-spin text-4xl text-gray-400" />}
        />
      </div>

      {pendingDelete && (
        <DeleteConfirmModal
          payment={pendingDelete}
          onConfirm={handleConfirmDelete}
          onCancel={handleCancelDelete}
          deleting={deleting}
        />
      )}

      {bulkDownload && (
        <BulkInvoiceDownloadModal
          isOpen
          onClose={handleCloseBulkDownload}
          total={bulkDownload.total}
          completed={bulkDownload.completed}
          failed={bulkDownload.failed}
          isDone={bulkDownload.isDone}
          isDownloading={bulkDownload.isDownloading}
        />
      )}
    </>
  );
};

export default TransactionTable;
